// Company self-service sign-up and profile. Split out of role.ts: these are
// company account facts, not role postings.
import { z } from 'zod';

import { PASSWORD_MIN_LENGTH, acceptPrivacySchema } from './auth.ts';
import { displayNameSchema } from './text.ts';

export const companySignupSchema = z.object({
  companyName: displayNameSchema,
  website: z
    .url({ protocol: /^https?$/ })
    .max(300)
    .nullable(),
  hqCountry: z.string().trim().length(2).toUpperCase().nullable(),
  fullName: displayNameSchema,
  email: z.email().trim().toLowerCase(),
  password: z.string().min(PASSWORD_MIN_LENGTH).max(128),
  turnstileToken: z.string().min(1),
  acceptPrivacy: acceptPrivacySchema,
  // Required by the API only while billing is paused (Q65); the form shows it then.
  acceptBillingNotice: z.boolean().optional(),
});

export const COMPANY_STAGES = [
  'pre_seed',
  'seed',
  'series_a',
  'series_b_plus',
  'bootstrapped',
  'public',
  'other',
] as const;
export const COMPANY_SIZE_BANDS = ['1-10', '11-50', '51-200', '201-1000', '1000+'] as const;
export const companyProfileSchema = z.object({
  name: displayNameSchema,
  website: z
    .url({ protocol: /^https?$/ })
    .max(300)
    .nullable(),
  stage: z.enum(COMPANY_STAGES).nullable(),
  sizeBand: z.enum(COMPANY_SIZE_BANDS).nullable(),
  hqCountry: z.string().trim().length(2).toUpperCase().nullable(),
  industryId: z.uuid().nullable(),
  description: z.string().trim().max(2000).nullable(),
});
export type CompanyProfileInput = z.infer<typeof companyProfileSchema>;

// The signed-in person's own display name — company_user.full_name, mirrored
// from Better Auth's user.name on save (see routes/company/account.ts).
export const companyUserNameSchema = z.object({
  fullName: displayNameSchema,
});

// Company profile page saves the company entity and the signed-in user's own
// name together under one Save button, even though they hit different
// tables server-side (see routes/company/account.ts) — one schema for that
// one form.
export const companyProfileWithNameSchema = companyProfileSchema.extend(
  companyUserNameSchema.shape,
);
export type CompanyProfileWithNameInput = z.infer<typeof companyProfileWithNameSchema>;

// Logo upload — same bucket as candidate CVs (services/logo-storage.ts),
// different key prefix. Small size cap since it's rendered inline.
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
// Raster only: SVG can carry script and would be served from the API origin.
export const LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

// A curator gives a company role credits without a Polar order (partner deal,
// goodwill, a refund by hand). The note is optional; empty means none.
export const GRANT_CREDITS_MAX = 20;
export const grantCreditsSchema = z.object({
  credits: z.number().int().min(1).max(GRANT_CREDITS_MAX),
  note: z.string().trim().max(500),
});
export type GrantCreditsInput = z.infer<typeof grantCreditsSchema>;

/**
 * Account fee gate is met by the paid webhook, a curator waiver, or activation
 * while billing was paused (Q65).
 */
export function isAccountFeeSettled(c: {
  accountFeePaidAt: string | null;
  feeWaivedAt: string | null;
  feeExemptAt: string | null;
}): boolean {
  return !!(c.accountFeePaidAt || c.feeWaivedAt || c.feeExemptAt);
}
